import { RealEstateDto } from "api/real-estate";
import { Checkbox } from "components/forms/fragments/Checkbox";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { FC, useEffect, useState } from "react";

import { realEstateAPI } from "services/swagger-api/realEstateAPI";
import { formatAddress } from "../../utils/realEstateFormatters";

interface BienPickerProps {
  /** Propriétaire dont on liste les biens. */
  personId: string;
  /** Si défini, exclut les biens déjà rattachés à ce parc. */
  excludeParcId?: string;
  /** Notifie le parent à chaque changement de sélection (liste d'IDs). */
  onSelectionChange: (ids: string[]) => void;
}

const PAGE_SIZE = 10;

export const BienPicker: FC<BienPickerProps> = ({ personId, excludeParcId, onSelectionChange }) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);

  const [items, setItems] = useState<RealEstateDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [allIds, setAllIds] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Debounce de la recherche
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(handle);
  }, [search]);

  // Tous les IDs candidats (léger) — base du « tout sélectionner » et du compteur total.
  useEffect(() => {
    let cancelled = false;
    realEstateAPI
      .getBienIdsByProprietaire({ personneId: personId, excludeParcId })
      .then((res) => { if (!cancelled) setAllIds(res.ids ?? []); })
      .catch((error) => console.error("Erreur lors du chargement des IDs de biens:", error));
    return () => { cancelled = true; };
  }, [personId, excludeParcId]);

  // Page courante (affichage)
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    realEstateAPI
      .searchBiensByProprietaire({
        personneId: personId,
        excludeParcId,
        getGenericListCommandDto: { searchString: debouncedSearch, pageNumber: page, pageSize: PAGE_SIZE }
      })
      .then((res) => {
        if (cancelled) return;
        setItems(res.items ?? []);
        setTotalCount(res.totalCount ?? 0);
      })
      .catch((error) => { if (!cancelled) console.error("Erreur lors du chargement des biens:", error); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [personId, excludeParcId, debouncedSearch, page]);

  useEffect(() => {
    onSelectionChange(Array.from(selected));
  }, [selected, onSelectionChange]);

  const toggleId = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(allIds));
  const clearAll = () => setSelected(new Set());

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <div className="space-y-3">
      {/* Recherche + actions de masse */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un bien, une adresse…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
        </div>
        <button type="button" onClick={selectAll} className="text-sm font-medium text-primary hover:underline whitespace-nowrap">
          Tout sélectionner ({allIds.length})
        </button>
        <button type="button" onClick={clearAll} className="text-sm text-gray-500 hover:underline whitespace-nowrap">
          Tout décocher
        </button>
      </div>

      <div className="text-xs text-gray-500">{selected.size} bien(s) sélectionné(s)</div>

      {/* Liste paginée */}
      <div className="min-h-[20rem] border rounded-lg divide-y">
        {isLoading && (
          <div className="px-4 py-8 text-center text-sm text-gray-400">Chargement…</div>
        )}
        {!isLoading && items.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-gray-500">Aucun bien disponible.</div>
        )}
        {!isLoading && items.map((bien) => (
          <div
            key={bien.id}
            onClick={() => toggleId(bien.id)}
            className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50"
          >
            <span onClick={(e) => e.stopPropagation()}>
              <Checkbox checked={selected.has(bien.id)} onChange={() => toggleId(bien.id)} />
            </span>
            <img
              src={bien.photoUrl || '/assets/images/real_estate_default.png'}
              alt={bien.name}
              className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
              loading="lazy"
            />
            <div className="flex flex-col min-w-0">
              <span className="font-medium text-gray-900 truncate">{bien.name}</span>
              <span className="text-xs text-gray-500 truncate">{formatAddress(bien.address)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>{totalCount} bien(s)</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
            >
              <ChevronLeft size={18} />
            </button>
            <span>{page} / {totalPages}</span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
